import { Box, Button, HStack, IconButton, Text } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { FiPlus } from 'react-icons/fi';

import {
  SubpagesLinksList,
  type SubpagesLinksListProps,
} from './subpages-block.tsx';

import { formatDocumentName } from '#src/utils/format-document-name';

export interface DocumentHeadingSubpagesPortalProps
  extends SubpagesLinksListProps {
  onCreateSubpage?: () => void;
  /** Match editor placeholder: soften path until the user edits a new path. */
  editorPlaceholderMuted?: boolean;
}

/** Path line, subpage toggle, create control, and portaled subpage links under the document heading. */
export function DocumentHeadingSubpagesPortal({
  parentPath,
  children: childDocs,
  onCreateSubpage,
  onNavigateToDocument,
  editorPlaceholderMuted = false,
}: DocumentHeadingSubpagesPortalProps) {
  const [subpagesExpanded, setSubpagesExpanded] = useState(true);
  const hasChildren = childDocs.length > 0;

  const pathLine = useMemo(() => {
    const p = parentPath.trim();
    if (p.length === 0) return '';
    return formatDocumentName(p).join(' › ');
  }, [parentPath]);

  const pathColor = editorPlaceholderMuted ? 'fg.subtle' : 'fg.muted';

  return (
    <Box mt={1}>
      <HStack
        className="editorjs-document-heading__breadcrumb-row"
        gap={0}
        align="center"
        flexWrap="wrap"
        justify="flex-start"
        aria-label="Document path"
      >
        <Text
          className="editorjs-document-heading__path-text"
          as="span"
          display="inline"
          fontSize="0.85rem"
          lineHeight="1.35"
          color={pathColor}
        >
          {pathLine}
        </Text>
        {hasChildren && (
          <>
            <Text
              as="span"
              aria-hidden
              flexShrink={0}
              fontSize="0.85rem"
              lineHeight="1.35"
              color="fg.subtle"
              px={1.5}
            >
              ›
            </Text>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              h="auto"
              minH={0}
              py={0}
              px={1}
              fontWeight="normal"
              color="fg.muted"
              aria-expanded={subpagesExpanded}
              flexShrink={0}
              onClick={() => setSubpagesExpanded((v) => !v)}
            >
              {subpagesExpanded ? 'Hide' : '...Show All'}
            </Button>
          </>
        )}
        <IconButton
          type="button"
          aria-label="Create subpage"
          variant="ghost"
          size="sm"
          flexShrink={0}
          disabled={typeof onCreateSubpage !== 'function'}
          onClick={onCreateSubpage}
        >
          <FiPlus />
        </IconButton>
      </HStack>

      {hasChildren && subpagesExpanded && (
        <Box mt={1}>
          <SubpagesLinksList
            parentPath={parentPath}
            children={childDocs}
            onNavigateToDocument={onNavigateToDocument}
          />
        </Box>
      )}
    </Box>
  );
}
